import { Card, Box, Typography, useMediaQuery, Tooltip as MuiTooltip } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip } from "recharts";

const renderInsideSmart = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent
}) => {
    if (!percent) return null;

    const RADIAN = Math.PI / 180;
    const baseRadius = innerRadius + (outerRadius - innerRadius) * 0.55;

    let x = cx + baseRadius * Math.cos(-midAngle * RADIAN);
    let y = cy + baseRadius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.06) {
        const smallRadius = outerRadius * 0.85;
        x = cx + smallRadius * Math.cos(-midAngle * RADIAN);
        y = cy + smallRadius * Math.sin(-midAngle * RADIAN);
    }

    return (
        <text
            x={x}
            y={y}
            fill="#fff"
            fontSize="11"
            fontWeight="600"
            textAnchor="middle"
            dominantBaseline="central"
        >
            {(percent * 100).toFixed(0)}%
        </text>
    );
};

export default function PieChartCard({ title, data }) {
    const isLargeScreen = useMediaQuery("(min-width:1700px)");

    const maxLength = isLargeScreen ? 45 : 35;
    return (
        <Card
            sx={{
                p: 2,
                borderRadius: "12px !important",
                background: "#fff",
                border: "1px solid #e5e5e5",
                boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
                height: "100%",
                position: "relative",
                overflow: "visible",
            }}
        >
            {/* HEADER WITH TOOLTIP */}
            <MuiTooltip
                title={title?.length > 25 ? title : ""}
                arrow
                placement="top"
                disableHoverListener={title?.length <= 25}
                componentsProps={{
                    tooltip: {
                        sx: {
                            maxWidth: "420px",       //increase width
                            fontSize: "14px",
                            lineHeight: 1.4,
                            whiteSpace: "normal",    //allow wrapping
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "-10px",
                        left: "12px",
                        right: "12px",
                        background: "#115f9c",
                        height: "36px",
                        borderRadius: "4px !important",
                        display: "flex",
                        alignItems: "center",
                        px: "12px",
                        color: "#fff",
                        fontSize: { xs: 12, sm: 12, md: 16 },
                        fontWeight: 200,
                        cursor: "pointer",
                        boxShadow: "0px 1px 16px rgba(128, 203, 255, 0.5)",
                        zIndex: 2
                    }}
                >
                    <Box sx={{
                        width: '100%', whiteSpace: "nowrap",        // force single line
                        overflow: "hidden",          // hide overflow
                        textOverflow: "ellipsis",    // show ...
                        lineHeight: "36px",
                    }}>  {title?.length > maxLength
                        ? `${title.slice(0, maxLength)}..`
                        : title}</Box>
                </Box>
            </MuiTooltip>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <PieChart width={260} height={260}>
                    <Tooltip
                        content={({ payload }) => {
                            if (!payload || payload.length === 0) return null;
                            const item = payload[0].payload;

                            return (
                                <div
                                    style={{
                                        background: "#fff",
                                        padding: "8px 12px",
                                        borderRadius: "8px",
                                        boxShadow: "0 0 8px rgba(0,0,0,0.15)",
                                        fontSize: "13px"
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span
                                            style={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: "50%",
                                                background: item.color
                                            }}
                                        />
                                        <strong>{item.label}</strong>
                                    </div>

                                    <div style={{ marginTop: 4 }}>
                                        {item.value}%
                                    </div>
                                </div>
                            );
                        }}
                    />

                    <Pie
                        data={data}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={0}
                        outerRadius={100}
                        label={renderInsideSmart}
                        labelLine={false}
                        startAngle={90}
                        endAngle={-270}
                        paddingAngle={0}
                        stroke="none"
                    >
                        {data.map((d, i) => (
                            <Cell key={i} fill={d.color} />
                        ))}
                    </Pie>
                </PieChart>
            </Box>

            {/* LEGEND */}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: 1.5,
                    mt: 1,
                }}
            >
                {data.map((item, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: item.color
                            }}
                        />
                        <Typography sx={{ fontSize: 10, color: "#111111", fontWeight: 200 }}>
                            {item.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Card>
    );
}
